import React, { useCallback } from 'react';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { useModal } from 'react-native-modalfy';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { Separator } from '../../common/Separator';
import { Post } from '@models/Posts/PostModel';
import { ToastService } from '@services';
import { IS_WEB, parseApiError, passIf } from '@utils';
import { MainStackParamListType, screens } from '@navigation';
import { modals, ModalStackParamsType } from '@modals';
import { ResponsiveIconTouchable } from '../../common/ResponsiveIconTouchable';
import { User } from '@models/Viewer/UserModel';

const Container = styled(View)({
  flexDirection: 'row',
  justifyContent: ['space-between', 'flex-end'],
});

const ActionGroupContainer = styled(View)({
  flexDirection: 'row',
});

export interface IPostActionPanelProps {
  item: Post;
}

type NavigationPropType =
  NativeStackNavigationProp<MainStackParamListType>;

export const PostActionPanel = observer<IPostActionPanelProps>(
  ({ item }) => {
    const { t } = useTranslation();
    const nav = useNavigation<NavigationPropType>();
    const modal = useModal<ModalStackParamsType>();

    async function toggleLike() {
      try {
        await item.itemData.toggleLike.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function navigateToComments() {
      if (IS_WEB) {
        modal.openModal(modals.PostViewModal, {
          item,
          onNavigateToUser,
        });
      } else {
        // TODO: add types to navigation and maybe move this function in another place
        nav.navigate(screens.Comment, {
          list: item.comments,
        });
      }
    }

    const onNavigateToUser = useCallback((user: User) => {
      nav.push(screens.UserProfile, {
        id: user.id,
      });
    }, []);

    function navigateToLikes() {
      if (IS_WEB) {
        modal.openModal(modals.LikesModal, {
          item,
          onNavigateToUser,
        });
      } else {
        nav.push(screens.Likes, {
          list: item.likes,
        });
      }
    }

    const likeIconName = item.itemData.userStats?.isLiked
      ? 'heart-bold'
      : 'heart-light';

    const likesCount = t('common.postItem.like', {
      count: item.itemData.likes,
    });
    const commentsCount = t('common.postItem.comment', {
      count: item.comments.totalCount,
    });

    return (
      <Container>
        <ActionGroupContainer>
          <ResponsiveIconTouchable
            onPress={toggleLike}
            onPressTitle={navigateToLikes}
            name={likeIconName}
            color={passIf(
              item.itemData.userStats?.isLiked,
              '$error300',
            )}
            title={likesCount}
            isResponsiveTitle={false}
          />
          <Separator size="$8" vertical />
          <ResponsiveIconTouchable
            onPress={navigateToComments}
            name="comment-light"
            title={commentsCount}
            isResponsiveTitle={false}
          />
        </ActionGroupContainer>
      </Container>
    );
  },
);
